import {useLayoutEffect, useRef, type CSSProperties} from 'react'
import {gsap} from 'gsap'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'

import styles from './MonaFooterGrid.module.css'
import type {MonaFooterProps} from './types'

import tileGradient1 from './tile-gradient-1.webp'
import tileGradient2 from './tile-gradient-2.webp'

const RECT_COLUMNS = 6
const RECT_ROWS = 4

const BACKGROUNDS = ['#FF80D2', '#0FBF3E', '#C898FD'] as const

const COLOR_TILES_FIRST = [
  {col: 0, row: 3, color: BACKGROUNDS[0]},
  {col: 1, row: 3, color: tileGradient1},
  {col: 1, row: 2, color: BACKGROUNDS[1]},
  {col: 2, row: 2, color: BACKGROUNDS[2]},
  {col: 2, row: 1, color: tileGradient2},
  {col: 3, row: 1, color: tileGradient1},
  {col: 3, row: 0, color: BACKGROUNDS[0]},
  {col: 4, row: 0, color: BACKGROUNDS[1]},
]

let repositoryIndex = 0
const GRID_CELLS = Array.from({length: RECT_COLUMNS * RECT_ROWS}, (_, index) => {
  const colIndex = index % RECT_COLUMNS
  const rowIndex = Math.floor(index / RECT_COLUMNS)
  let grayOpacity = 0
  if ((colIndex + rowIndex) % 4 === 2) {
    grayOpacity = 11
  } else if ((colIndex + rowIndex) % 4 === 1) {
    grayOpacity = 4
  }
  const colorTile = COLOR_TILES_FIRST.find(tile => tile.col === colIndex && tile.row === rowIndex)
  const color = colorTile?.color ?? 'transparent'
  const colorBackground = color === tileGradient1 || color === tileGradient2 ? `url(${color})` : color

  return {
    id: `cell-${colIndex}-${rowIndex}`,
    colIndex,
    rowIndex,
    repositoryIndex: grayOpacity === 0 ? repositoryIndex++ : null,
    grayStyle: {
      '--color': grayOpacity === 0 ? 'transparent' : `color-mix(in srgb, #8D8D8D ${grayOpacity}%, transparent)`,
    } satisfies CSSProperties & {'--color': string},
    colorStyle: {
      '--color': colorBackground,
    } satisfies CSSProperties & {'--color': string},
  }
})

const REPOSITORY_REVEAL_CELLS = GRID_CELLS.filter(cell => cell.repositoryIndex !== null).sort(
  (a, b) =>
    a.colIndex + (RECT_ROWS - 1 - a.rowIndex) - (b.colIndex + (RECT_ROWS - 1 - b.rowIndex)) || b.rowIndex - a.rowIndex,
)

function repositoryHref(url: string) {
  const parsed = new URL(url, 'https://github.com')
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    throw new TypeError('Featured repository URLs must use HTTP or HTTPS')
  }
  return parsed.href
}

export function MonaFooterGrid({animate = false, repos}: {animate?: boolean; repos?: MonaFooterProps['repos']}) {
  const gridRef = useRef<HTMLDivElement>(null)
  const prefersReducedMotion = usePrefersReducedMotion()

  useLayoutEffect(() => {
    const grid = gridRef.current
    if (!grid) return

    const context = gsap.context(() => {
      const panels = REPOSITORY_REVEAL_CELLS.flatMap(cell => {
        const panel = grid.querySelector(`[data-cell="${cell.id}"] .${styles.RepositoryPanel}`)
        return panel ? [panel] : []
      })
      const colorTiles = COLOR_TILES_FIRST.map(({col, row}) => `[data-cell="cell-${col}-${row}"] .${styles.ColorTile}`)
      if (prefersReducedMotion) {
        gsap.set(colorTiles, {'--opacity': 0})
        if (panels.length > 0) gsap.set(panels, {autoAlpha: 1})
        gsap.set(grid, {attr: {'data-repositories-ready': 'true'}})
        return
      }
      if (!animate) return

      const tl = gsap.timeline({
        delay: 0.8,
      })
      tl.to(colorTiles, {
        '--opacity': 1,
        duration: 0.6,
        stagger: {each: 0.3, from: 'start'},
        ease: 'power2.out',
      })
      tl.to(colorTiles, {
        '--opacity': 0,
        duration: 0.6,
        stagger: {each: 0.1, from: 'start'},
        ease: 'power2.out',
      })
      if (panels.length > 0) {
        tl.to(
          panels,
          {
            autoAlpha: 1,
            duration: 0.6,
            stagger: 0.12,
            ease: 'power2.out',
          },
          '-=0.8',
        )
        tl.set(grid, {attr: {'data-repositories-ready': 'true'}})
      }
    }, grid)

    return () => context.revert()
  }, [animate, prefersReducedMotion, repos?.items])

  return (
    <div className={styles.MonaFooterGrid} ref={gridRef}>
      {GRID_CELLS.map(cell => {
        const repository = cell.repositoryIndex === null ? undefined : repos?.items[cell.repositoryIndex]
        return (
          <div key={cell.id} data-cell={cell.id} className={styles.Rect}>
            <div className={styles.GrayTile} style={cell.grayStyle} />
            <div className={styles.ColorTile} style={cell.colorStyle} />
            {repository && repos && (
              <a className={styles.RepositoryPanel} href={repositoryHref(repository.url)}>
                <div className={styles.RepositoryContent}>
                  <p className={styles.RepositoryName}>{repository.nameWithOwner.replace('/', ' / ')}</p>
                  {repository.description && <p className={styles.RepositoryDescription}>{repository.description}</p>}
                  <p className={styles.RepositoryStars}>
                    {repos.starsTemplate.replaceAll('{count}', String(repository.newStars))}
                  </p>
                </div>
              </a>
            )}
          </div>
        )
      })}
    </div>
  )
}
